import React, { useContext, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton,
    Box, Chip, List, ListItem, ListItemButton, ListItemText, Collapse
} from '@mui/material';
import { Close, ContactPage, Fullscreen, FullscreenExit, ExpandMore, ChevronRight } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { UserPreferencesContext } from '../../common/UserPreferences';

/**
 * SCREEN_CONFIG drives the extensible preferences viewer with hierarchical navigation.
 * Structure: { [TAB]: { label, Icon?, children: { [SCREEN_KEY]: { label, Icon, columns } } } }
 * To add a future screen, add it under the appropriate tab's children.
 */
const SCREEN_CONFIG = {
    HOME: {
        label: 'Home',
        children: {
            HOME_DASHBOARD: {
                label: 'Home Dashboard',
                prefsKey: 'HOME',
                Icon: ContactPage,
                columns: [
                    { mapping: 'id',                    name: 'DSAR ID' },
                    { mapping: 'requestId',             name: 'Request ID' },
                    { mapping: 'ticket_type',           name: 'Request Type' },
                    { mapping: 'requestor',             name: 'Requestor' },
                    { mapping: 'status',                name: 'Request Status' },
                    { mapping: 'created_date',          name: 'Create Date Time' },
                    { mapping: 'downstreamApps',        name: 'DownStream Apps' },
                    { mapping: 'app',                   name: 'Downstream Apps' },
                    { mapping: 'from_date',             name: 'From Date' },
                    { mapping: 'to_date',               name: 'To Date' },
                    { mapping: 'response_contains',     name: 'Response Contains' },
                    { mapping: 'request_contains',      name: 'Request Contains' },
                    { mapping: 'noOfVins',              name: 'VIN(s) #' },
                    { mapping: 'noOfGUIDS',             name: 'GUID(s) #' },
                    { mapping: 'noOfCustIds',           name: 'Cust ID(s) #' },
                ]
            }
        }
    },
    ADMIN: {
        label: 'Admin',
        children: {
            CONTACTS: {
                label: 'Contacts',
                navLabel: 'Contacts',
                Icon: ContactPage,
                columns: [
                    { mapping: 'app_name',                  name: 'Application Name' },
                    { mapping: 'app_status',                name: 'App Status' },
                    { mapping: 'primary_contact_name',      name: 'Primary Contact Name' },
                    { mapping: 'primary_contact_email',     name: 'Primary Contact Email' },
                    { mapping: 'escalation_contact_name',   name: 'Escalation Contact Name' },
                    { mapping: 'escalation_contact_email',  name: 'Escalation Contact Email' },
                    { mapping: 'team_email',                name: 'Team Email' },
                ]
            },
            DATA_DICTIONARY: {
                label: 'Data Dictionary',
                prefsKey: 'DATA_DICTIONARY',
                Icon: ContactPage,
                columns: [
                    { mapping: 'display_name',              name: 'Source' },
                    { mapping: 'element_type',              name: 'Element Type' },
                    { mapping: 'key',                       name: 'Key' },
                    { mapping: 'display_term',              name: 'Display Term' },
                    { mapping: 'description',               name: 'Description' },
                    { mapping: 'display_term_fr',           name: 'Display Term French' },
                    { mapping: 'description_fr',            name: 'Description French' },
                    { mapping: 'data_type',                 name: 'Data Type' },
                    { mapping: 'review_category',           name: 'Review Category' },
                    { mapping: 'tips',                      name: 'TIPS' },
                    { mapping: 'qb64',                      name: 'QB64' },
                    { mapping: 'overwrite',                 name: 'Revise Attribute Value' },
                    { mapping: 'canned_message',            name: 'Updated Attribute Value' },
                    { mapping: 'notes',                     name: 'Notes' },
                    { mapping: 'applicable_requestor_string', name: 'Applicable Requestor' },
                    { mapping: 'is_active',                 name: 'Is Active' },
                ]
            }
        }
    }
};

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(30,58,95,0.22)',
    },
    dialogTitle: {
        background: 'linear-gradient(135deg, #1e3a5f 0%, #267db7 125%, #3b9edd 100%)',
        color: '#fff',
        fontWeight: 700,
        letterSpacing: '1.5px',
        fontSize: '0.9rem',
        padding: '14px 20px',
        textTransform: 'uppercase',
        position: 'relative',
    },
    closeButton: {
        position: 'absolute',
        right: 10,
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#fff',
        '&:hover': {
            background: 'rgba(255,255,255,0.15)',
        },
    },
    contentWrapper: {
        display: 'flex',
        height: 520,
        padding: 0,
        overflow: 'hidden',
        background: '#f8fafc',
    },
    sidebar: {
        width: 200,
        minWidth: 200,
        borderRight: '1px solid #dde4ef',
        background: '#f0f4f9',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    sidebarHeader: {
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '1.5px',
        color: '#fff',
        textTransform: 'uppercase',
        padding: '10px 16px 9px',
        background: '#778899',
    },
    mainContent: {
        flex: 1,
        padding: '20px 24px',
        overflowY: 'auto',
        background: '#f8fafc',
    },
    screenTitle: {
        fontWeight: 700,
        fontSize: '1rem',
        color: '#1e3a5f',
        marginBottom: '18px',
        paddingBottom: '10px',
        borderBottom: '2px solid #267db7',
        letterSpacing: '0.3px',
    },
    section: {
        border: '1px solid #dde4ef',
        borderRadius: '6px',
        marginBottom: '14px',
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(30,58,95,0.07)',
    },
    sectionHeader: {
        background: 'linear-gradient(135deg, #1e3a5f 0%, #267db7 125%, #3b9edd 100%)',
        padding: '7px 14px',
        fontWeight: 600,
        fontSize: '0.78rem',
        color: '#fff',
        letterSpacing: '0.4px',
    },
    sectionBody: {
        padding: '10px 14px',
        minHeight: 44,
        background: '#fff',
    },
    subLabel: {
        fontSize: '0.75rem',
        color: '#daa520',
        marginBottom: '8px',
        fontStyle: 'italic',
        fontWeight: 500,
    },
    chipRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    columnsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        rowGap: '5px',
        columnGap: '16px',
    },
    columnItem: {
        fontSize: '0.8rem',
        color: '#334155',
    },
    columnHidden: {
        fontSize: '0.8rem',
        color: '#94a3b8',
        textDecoration: 'line-through',
        fontStyle: 'italic',
    },
    sortValueBox: {
        display: 'inline-block',
        background: '#f0f4f9',
        border: '1px solid #dde4ef',
        borderRadius: '4px',
        padding: '5px 12px',
        fontSize: '0.82rem',
        color: '#1e3a5f',
        minWidth: '140px',
    },
    radioRow: {
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
    },
    radioOption: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.82rem',
        color: '#334155',
        cursor: 'default',
        '& input[type="radio"]': {
            cursor: 'default',
            accentColor: '#267db7',
        },
    },
    emptyHint: {
        fontSize: '0.78rem',
        color: '#94a3b8',
        fontStyle: 'italic',
    },
    footerBar: {
        borderTop: '1px solid #dde4ef',
        padding: '9px 24px',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #267db7 125%, #3b9edd 100%)',
        fontSize: '0.78rem',
        fontStyle: 'italic',
        color: 'rgba(255,255,255,0.85)',
        '& strong': {
            color: '#daa520',
        },
    },
    treeItem: {
        paddingLeft: '13px',
    },
    treeItemButton: {
        padding: '6px 8px',
        fontSize: '0.82rem',
    },
    treeItemExpandIcon: {
        marginRight: '4px',
        transition: 'transform 0.2s',
    },
    treeItemExpandIconOpen: {
        transform: 'rotate(90deg)',
    },
    filterLabelHighlight: {
        fontWeight: 600,
        color: '#1e3a5f',
        marginRight: '4px',
    },
    filterValueText: {
        color: '#334155',
    },
}));

function PreferencesModal({ open, onClose }) {
    const classes = useStyles();
    const { getPreferences, loaded } = useContext(UserPreferencesContext);

    const [isMaximized, setIsMaximized] = useState(false);
    const [activeScreen, setActiveScreen] = useState('REQUESTS');
    const [expandedTabs, setExpandedTabs] = useState({ HOME: true, ADMIN: false });

    // Flatten screen config to find the active screen
    let screenConf = null;
    Object.values(SCREEN_CONFIG).forEach((tab) => {
        if (tab.children && tab.children[activeScreen]) {
            screenConf = tab.children[activeScreen];
        }
    });

    if (!screenConf) {
        // Fallback to first available screen
        const firstTab = Object.values(SCREEN_CONFIG)[0];
        const firstScreen = firstTab.children && Object.entries(firstTab.children)[0];
        if (firstScreen) {
            screenConf = firstScreen[1];
        }
    }

    const prefs = loaded && screenConf
        ? getPreferences(screenConf.prefsKey || activeScreen)
        : { filters: {}, sort: {}, hiddenColumns: {}, pageCount: 20 };

    const { filters = {}, sort = {}, hiddenColumns = {}, pageCount = 20 } = prefs;

    // Resolve a column's display name from its field mapping
    const resolveColumnName = (mapping) => {
        if (!screenConf) return mapping;
        const col = screenConf.columns.find((c) => c.mapping === mapping);
        return col ? col.name : mapping;
    };

    // All columns with visibility status
    const allColumns = screenConf ? screenConf.columns : [];
    const visibleColumns = allColumns.filter((col) => !hiddenColumns[col.mapping]);
    const hiddenColumnsList = allColumns.filter((col) => hiddenColumns[col.mapping]);

    // Active filter entries that actually carry a value
    const activeFilterEntries = Object.entries(filters).filter(([, f]) => {
        if (!f?.value) return false;
        if (Array.isArray(f.value)) return f.value.filter((v) => v !== 'All').length > 0;
        return String(f.value).trim().length > 0;
    });

    const renderFilterChip = (field, filter) => {
        const colLabel = resolveColumnName(field);
        const values = Array.isArray(filter.value)
            ? filter.value.filter((v) => v !== 'All')
            : [filter.value];
        
        return (
            <div key={field} style={{ marginBottom: '10px' }}>
                <div className={classes.filterLabelHighlight} style={{ marginBottom: '5px' }}>
                    {colLabel}:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {values.map((val) => (
                        <Chip
                            key={`${field}-${val}`}
                            label={val}
                            size="small"
                            variant="outlined"
                            sx={{ 
                                fontSize: '0.7rem', 
                                background: 'rgba(38,125,183,0.07)', 
                                borderColor: '#267db7', 
                                color: '#334155', 
                                fontWeight: 500,
                                height: '24px',
                                '& .MuiChip-label': { padding: '0 8px' }
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    };

    const toggleTabExpansion = (tab) => {
        setExpandedTabs((prev) => ({
            ...prev,
            [tab]: !prev[tab],
        }));
    };

    const renderTree = () => {
        return Object.entries(SCREEN_CONFIG).map(([tabKey, tabConf]) => (
            <div key={tabKey}>
                <ListItemButton
                    onClick={() => toggleTabExpansion(tabKey)}
                    className={classes.treeItemButton}
                    sx={{
                        background: 'rgba(38,125,183,0.06)',
                        borderLeft: '3px solid transparent',
                        fontWeight: 600,
                        fontSize: '0.83rem',
                        color: '#1e3a5f',
                    }}
                >
                    <ChevronRight
                        className={`${classes.treeItemExpandIcon} ${expandedTabs[tabKey] ? classes.treeItemExpandIconOpen : ''}`}
                        fontSize="small"
                        sx={{ color: '#267db7' }}
                    />
                    {tabConf.label}
                </ListItemButton>
                <Collapse in={expandedTabs[tabKey]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {tabConf.children && Object.entries(tabConf.children).map(([screenKey, screenConf]) => {
                            const Icon = screenConf.Icon;
                            return (
                                <ListItem key={screenKey} disablePadding className={classes.treeItem}>
                                    <ListItemButton
                                        selected={activeScreen === screenKey}
                                        onClick={() => setActiveScreen(screenKey)}
                                        className={classes.treeItemButton}
                                        sx={{
                                            '&.Mui-selected': {
                                                background: 'rgba(38,125,183,0.12)',
                                                borderLeft: '3px solid #267db7',
                                                '& .MuiListItemText-primary': { fontWeight: 700, color: '#1e3a5f' },
                                            },
                                            '&.Mui-selected:hover': {
                                                background: 'rgba(38,125,183,0.18)',
                                            },
                                            '&:not(.Mui-selected)': {
                                                borderLeft: '3px solid transparent',
                                            },
                                            '&:hover': {
                                                background: 'rgba(38,125,183,0.07)',
                                            },
                                            padding: '7px 13px',
                                        }}
                                    >
                                        {Icon && (
                                            <Icon
                                                fontSize="small"
                                                sx={{
                                                    mr: 1,
                                                    color: activeScreen === screenKey ? '#267db7' : '#94a3b8',
                                                    fontSize: '1.1rem',
                                                }}
                                            />
                                        )}
                                        <ListItemText
                                            primary={screenConf.navLabel || screenConf.label}
                                            primaryTypographyProps={{ fontSize: '0.82rem' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            </div>
        ));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMaximized ? false : "md"}
            fullWidth={!isMaximized}
            fullScreen={isMaximized}
            PaperProps={{ className: classes.dialogPaper }}
        >
            {/* Header */}
            <DialogTitle className={classes.dialogTitle} disableTypography>
                Global System Preferences
                <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: '4px' }}>
                    <IconButton
                        size="small"
                        onClick={() => setIsMaximized(!isMaximized)}
                        sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
                        aria-label={isMaximized ? "exit fullscreen" : "enter fullscreen"}
                    >
                        {isMaximized ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
                        aria-label="close preferences"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Body */}
            <DialogContent className={classes.contentWrapper}>
                {/* Left sidebar — module tree */}
                <div className={classes.sidebar}>
                    <div className={classes.sidebarHeader}>Modules</div>
                    <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
                        {renderTree()}
                    </List>
                </div>

                {/* Right panel */}
                {screenConf && (
                    <div className={classes.mainContent}>
                        <div className={classes.screenTitle}>
                            {screenConf.label} Module Preferences
                        </div>

                        {/* 1. Selected Filter Parameters */}
                        <div className={classes.section}>
                            <div className={classes.sectionHeader}>1. Selected Filter Parameters</div>
                            <div className={classes.sectionBody}>
                                {activeFilterEntries.length === 0 ? (
                                    <span className={classes.emptyHint}>No filter parameters saved</span>
                                ) : (
                                    <>
                                        <div className={classes.subLabel}>
                                            Filter {screenConf.label} By:
                                        </div>
                                        <div className={classes.chipRow}>
                                            {activeFilterEntries.map(([field, filter]) => renderFilterChip(field, filter))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 2. Columns selected to display (with hidden columns) */}
                        <div className={classes.section}>
                            <div className={classes.sectionHeader}>2. Columns selected to display</div>
                            <div className={classes.sectionBody}>
                                {allColumns.length === 0 ? (
                                    <span className={classes.emptyHint}>No columns available</span>
                                ) : (
                                    <>
                                        <div className={classes.subLabel}>Choose Columns:</div>
                                        <div className={classes.columnsGrid}>
                                            {visibleColumns.map((col) => (
                                                <span key={col.mapping} className={classes.columnItem}>
                                                    {col.name}
                                                </span>
                                            ))}
                                        </div>
                                        {hiddenColumnsList.length > 0 && (
                                            <>
                                                <div className={classes.subLabel} style={{ marginTop: '12px', marginBottom: '6px' }}>
                                                    Hidden Columns:
                                                </div>
                                                <div className={classes.columnsGrid}>
                                                    {hiddenColumnsList.map((col) => (
                                                        <span key={col.mapping} className={classes.columnHidden}>
                                                            {col.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Sections 3 & 4 side by side */}
                        <Box display="flex" gap="14px">
                            {/* 3. Sort column */}
                            <div className={classes.section} style={{ flex: 1 }}>
                                <div className={classes.sectionHeader}>3. Sort column</div>
                                <div className={classes.sectionBody}>
                                    <div className={classes.subLabel}>Select Column to Sort By:</div>
                                    <span className={classes.sortValueBox}>
                                        {sort?.field
                                            ? resolveColumnName(sort.field)
                                            : <em style={{ color: '#94a3b8', fontStyle: 'italic' }}>None</em>
                                        }
                                    </span>
                                </div>
                            </div>

                            {/* 4. Order of sort */}
                            <div className={classes.section} style={{ flex: 1 }}>
                                <div className={classes.sectionHeader}>4. Order of sort</div>
                                <div className={classes.sectionBody}>
                                    <div className={classes.subLabel}>Choose Sorting Direction:</div>
                                    <div className={classes.radioRow}>
                                        <label className={classes.radioOption}>
                                            <input
                                                type="radio"
                                                readOnly
                                                checked={!sort?.sort || sort.sort === 'asc'}
                                                disabled
                                            />
                                            Ascending (A-Z)
                                        </label>
                                        <label className={classes.radioOption}>
                                            <input
                                                type="radio"
                                                readOnly
                                                checked={sort?.sort === 'desc'}
                                                disabled
                                            />
                                            Descending (Z-A)
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* 5. Records per page */}
                            <div className={classes.section} style={{ flex: 1 }}>
                                <div className={classes.sectionHeader}>5. Records per page</div>
                                <div className={classes.sectionBody}>
                                    <div className={classes.subLabel}>Page Size:</div>
                                    <span className={classes.sortValueBox}>
                                        {pageCount}
                                    </span>
                                </div>
                            </div>
                        </Box>
                    </div>
                )}
            </DialogContent>

            {/* Footer */}
            <div className={classes.footerBar}>
                View mode only.&nbsp; Save preferences on the respective screen using the
                <strong> Save</strong> button.
            </div>
        </Dialog>
    );
}

export default PreferencesModal;
